/**
 * aC Men's Hub のコーデ組み合わせ API contract（zod schema + 型 + 選定ロジック）。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * 画像生成の前に「どのアイテムでコーデを組むか」だけを確定させる層。生成は従量課金なので、
 * 何が作られるかを先に見せてから実行できるようにする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Owns the single implementation of how outfits are composed from usable stock, so the requester can see what will be generated before paying for image generation.
 * @graph-connects none
 */
import { z } from 'zod';
import { COORDE_REUSE_LIMIT, CoordeLayerSchema } from './coorde-capacity.js';
import { SeasonSchema } from './item-catalog.js';
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business One garment usable for outfit generation: the FKU plus the attributes needed to compose and to verify against the real item.
 * @graph-connects none
 */
export const CoordeOutfitItemSchema = z.object({
    fkuCode: z.string(),
    itemName: z.string(),
    brandName: z.string(),
    categoryName: z.string(),
    colorName: z.string(),
    layer: CoordeLayerSchema,
    availableCount: z.number(),
    frontImageUrl: z.string(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business One proposed outfit: the garments it is made of, ordered outer to bottom.
 * @graph-connects none
 */
export const CoordeOutfitSchema = z.object({
    index: z.number(),
    items: z.array(CoordeOutfitItemSchema),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Request for a set of outfits: which season, how many, and whether to include an outer layer.
 * @graph-connects none
 */
export const CoordePreviewRequestSchema = z.object({
    season: SeasonSchema,
    count: z.number().int().min(1).max(30),
    includeOuter: z.boolean(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Proposed outfits plus how many were actually composable, so a shortfall is visible before generation is paid for.
 * @graph-connects none
 */
export const CoordePreviewResponseSchema = z.object({
    season: SeasonSchema,
    requestedCount: z.number(),
    outfits: z.array(CoordeOutfitSchema),
    snapshotAt: z.string(),
});
/** 組み合わせの重複判定キー。 @graph-connects none */
function pairKey(item, other) {
    return `${item.fkuCode}::${other.fkuCode}`;
}
/**
 * 貸出可能数の多い順に並べる。同数なら FKU コード順で安定させる。
 *
 * 貸出可能数を優先するのは、素材を見て「これを借りたい」と思われたときに実際に在庫がある
 * 確率を上げるため。同数のときに FKU で決めるのは、同じ在庫状況なら毎回同じ結果を返して
 * 再現性を保つため。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Orders candidate garments so the ones most likely to be actually rentable are proposed first, deterministically.
 * @graph-connects none
 */
export function sortByAvailability(items) {
    return [...items].sort((a, b) => b.availableCount - a.availableCount || a.fkuCode.localeCompare(b.fkuCode));
}
/**
 * 使用回数が上限に達していない次のアイテムを、cursor から順に探す。
 * プールを1周しても見つからなければ null を返す。
 * @graph-connects none
 */
function takeNext(items, cursor, used, reuseLimit) {
    for (let step = 0; step < items.length; step += 1) {
        const index = (cursor + step) % items.length;
        const item = items[index];
        if (!item)
            continue;
        if ((used.get(item.fkuCode) ?? 0) < reuseLimit) {
            return { item, nextCursor: index + 1 };
        }
    }
    return null;
}
/**
 * 使用可能な在庫から、指定本数のコーデを組む。
 *
 * ルール:
 * - 同一 FKU の使用は 1 ラン内で `reuseLimit` 回まで（BR-3）
 * - トップスとボトムスの同じ組み合わせは 1 ラン内で 1 回だけ（同じ絵が2枚できるのを防ぐ）
 * - 在庫が尽きたら要求本数に満たなくても打ち切る。**足りない分を無理に埋めない**
 *   （同じ服ばかりの素材を量産するより、少なく返して依頼者に判断させる）
 *
 * アウターは 3 点構成のときだけ使う。アウターが尽きても 2 点でごまかさず打ち切るのは、
 * 依頼者が「3 点で頼んだのに 2 点が混ざる」ことを検品で見落とすため。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Composes as many distinct outfits as the usable stock allows, without exceeding the per-FKU reuse limit and without repeating a top-and-bottom pairing.
 * @graph-connects none
 */
export function selectOutfits(pool, request, reuseLimit = COORDE_REUSE_LIMIT) {
    const tops = sortByAvailability(pool.tops);
    const bottoms = sortByAvailability(pool.bottoms);
    const outer = sortByAvailability(pool.outer);
    const used = new Map();
    const seenPairs = new Set();
    const outfits = [];
    let topCursor = 0;
    let bottomCursor = 0;
    let outerCursor = 0;
    // 「このトップスでは新しい組み合わせが作れない」が全トップス分続いたら打ち切る。
    // カーソルの位置ではなく失敗回数で止めることで、必ず有限回で終わることを読み取れるようにする。
    let consecutiveFailures = 0;
    while (outfits.length < request.count) {
        const top = takeNext(tops, topCursor, used, reuseLimit);
        if (!top)
            break;
        // 同じトップスに対してボトムスを1周させ、未使用の組み合わせを探す
        let picked = null;
        let scanCursor = bottomCursor;
        // 各ボトムスを高々1回ずつ試す。1周して未使用の組み合わせが無ければ諦める
        let remaining = bottoms.length;
        while (remaining > 0) {
            remaining -= 1;
            const bottom = takeNext(bottoms, scanCursor, used, reuseLimit);
            if (!bottom)
                break;
            scanCursor = bottom.nextCursor;
            if (seenPairs.has(pairKey(top.item, bottom.item)))
                continue;
            picked = { top: top.item, bottom: bottom.item, cursor: bottom.nextCursor };
            break;
        }
        if (!picked) {
            // このトップスでは新しい組み合わせを作れない。次のトップスへ送る
            topCursor = top.nextCursor;
            consecutiveFailures += 1;
            if (consecutiveFailures >= tops.length)
                break;
            continue;
        }
        consecutiveFailures = 0;
        const items = [];
        if (request.includeOuter) {
            const picked3 = takeNext(outer, outerCursor, used, reuseLimit);
            if (!picked3)
                break;
            outerCursor = picked3.nextCursor;
            items.push(picked3.item);
            used.set(picked3.item.fkuCode, (used.get(picked3.item.fkuCode) ?? 0) + 1);
        }
        items.push(picked.top, picked.bottom);
        used.set(picked.top.fkuCode, (used.get(picked.top.fkuCode) ?? 0) + 1);
        used.set(picked.bottom.fkuCode, (used.get(picked.bottom.fkuCode) ?? 0) + 1);
        seenPairs.add(pairKey(picked.top, picked.bottom));
        outfits.push({ index: outfits.length + 1, items });
        topCursor = top.nextCursor;
        bottomCursor = picked.cursor;
    }
    return outfits;
}
//# sourceMappingURL=coorde-outfit.js.map